import { monthLabels } from './mockData';
import { formatNumber, formatValue } from './utils';

interface AxisTooltipParam {
  axisValue?: string | number;
  axisValueLabel?: string;
  dataIndex?: number;
  marker?: string;
  seriesName?: string;
  value?: unknown;
}

const escapeHtml = (value: unknown) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const buildAxisTooltip = (params: AxisTooltipParam | AxisTooltipParam[], unitMap: Record<string, string>) => {
  const list = Array.isArray(params) ? params : [params];
  const axisLabel = list[0]?.axisValueLabel ?? list[0]?.axisValue ?? '--';
  const rows = list
    .map(item => {
      const seriesName = item.seriesName ?? '--';
      const value = formatNumber(item.value);
      const unit = value === '--' ? '' : unitMap[seriesName] ?? '';
      return `<div style="display:flex;align-items:center;justify-content:space-between;gap:24px;line-height:24px;">
        <span style="min-width:0;text-align:left;white-space:nowrap;">${item.marker ?? ''}${escapeHtml(
        seriesName
      )}</span>
        <span style="flex:none;text-align:right;white-space:nowrap;">${value}${escapeHtml(unit)}</span>
      </div>`;
    })
    .join('');

  return `<div style="width:220px;box-sizing:border-box;">
    <div style="margin-bottom:4px;text-align:left;line-height:22px;">${escapeHtml(axisLabel)}</div>
    ${rows}
  </div>`;
};

const curtailmentTooltipUnitMap: Record<string, string> = {
  实际发电量: 'MWh',
  理论发电量: 'MWh',
  弃光量: 'MWh',
  弃光率: '%',
};

export const curtailmentOption = {
  color: ['#59D744', '#2D8DFF', '#FFCB47', '#FF9247'],
  tooltip: {
    trigger: 'axis',
    renderMode: 'html',
    confine: true,
    formatter: (params: AxisTooltipParam | AxisTooltipParam[]) => buildAxisTooltip(params, curtailmentTooltipUnitMap),
  },
  legend: {
    right: 6,
    top: 0,
    itemWidth: 16,
    itemHeight: 12,
    itemGap: 16,
    textStyle: { fontSize: 14 },
    data: [
      { name: '实际发电量', icon: 'path://M0,0H12V12H0Z M16,0L16,0' },
      { name: '理论发电量', icon: 'path://M0,0H12V12H0Z M16,0L16,0' },
      { name: '弃光量', icon: 'path://M0,0H12V12H0Z M16,0L16,0' },
      { name: '弃光率', icon: 'path://M0,4H16V8H0Z M0,0L0,0 M16,12L16,12' },
    ],
  },
  grid: { left: 48, right: 48, top: 48, bottom: 36 },
  xAxis: { type: 'category', data: monthLabels, axisTick: { show: false } },
  yAxis: [
    { type: 'value', name: 'MWh', splitNumber: 5 },
    {
      type: 'value',
      name: '%',
      splitNumber: 5,
      alignTicks: true,
      splitLine: { show: false },
    },
  ],
  series: [
    {
      name: '理论发电量',
      type: 'bar',
      barWidth: 12,
      itemStyle: { color: '#2D8DFF' },
      data: [1390, 1300, 1110, 940, 1010, 1130, 960, 950, 1120, 990, 950, 1110],
    },
    {
      name: '实际发电量',
      type: 'bar',
      stack: 'actualGeneration',
      barWidth: 12,
      itemStyle: { color: '#59D744' },
      data: [1000, 930, 610, 560, 620, 660, 590, 560, 650, 600, 560, 620],
    },
    {
      name: '弃光量',
      type: 'bar',
      stack: 'actualGeneration',
      barWidth: 12,
      itemStyle: { color: '#FFCB47' },
      data: [390, 370, 500, 380, 390, 470, 370, 390, 470, 390, 390, 490],
    },
    {
      name: '弃光率',
      type: 'line',
      yAxisIndex: 1,
      smooth: true,
      showSymbol: false,
      lineStyle: { width: 2, color: '#FF9247' },
      itemStyle: { color: '#FF9247' },
      data: [20, 21, 20, 22, 19, 23, 20, 19, 21, 20, 19, 20],
      markLine: {
        silent: true,
        symbol: 'none',
        data: [
          {
            yAxis: 27.68,
            lineStyle: { color: '#F04444', type: 'dashed' },
            label: { formatter: '基准弃光率:27.68%', color: '#F04444', position: 'insideEndTop' },
          },
          {
            xAxis: 1,
            lineStyle: { color: '#52E7FF', type: 'solid' },
            label: {
              formatter: '调度运行开始月',
              color: '#FFFFFF',
              backgroundColor: 'rgba(67, 130, 140, 0.9)',
              padding: [4, 8],
              position: 'insideEndTop',
              distance: 0,
              rotate: 0,
              align: 'center',
            },
          },
        ],
      },
    },
  ],
};

export interface CurtailmentTrendItem {
  period?: string;
  actualGeneration?: number;
  theoreticalGeneration?: number;
  curtailmentAmount?: number;
  curtailmentRate?: number;
  baselineCurtailmentRate?: number;
  isDispatchStartMonth?: boolean;
}

export const getCurtailmentOption = (trendData: CurtailmentTrendItem[]) => {
  const baselineRate = trendData.find(item => item.baselineCurtailmentRate != null)?.baselineCurtailmentRate;
  const dispatchStartIndex = trendData.findIndex(item => item.isDispatchStartMonth);
  const markLineData: Array<Record<string, unknown>> = [];

  if (baselineRate != null) {
    markLineData.push({
      yAxis: baselineRate,
      lineStyle: { color: '#F04444', type: 'dashed' },
      label: {
        formatter: `基准弃光率:${formatValue(baselineRate, '%', { useTenThousandUnit: false })}`,
        color: '#F04444',
        position: 'insideEndTop',
      },
    });
  }

  if (dispatchStartIndex >= 0) {
    markLineData.push({
      xAxis: dispatchStartIndex,
      lineStyle: { color: '#52E7FF', type: 'solid' },
      label: {
        formatter: '调度运行开始月',
        color: '#FFFFFF',
        backgroundColor: 'rgba(67, 130, 140, 0.9)',
        padding: [4, 8],
        position: 'insideEndTop',
        distance: 0,
        rotate: 0,
        align: 'center',
      },
    });
  }

  return {
    ...curtailmentOption,
    xAxis: {
      ...curtailmentOption.xAxis,
      data: trendData.map(item => item.period ?? '--'),
    },
    series: curtailmentOption.series.map(series => {
      if (series.name === '理论发电量') {
        return { ...series, data: trendData.map(item => item.theoreticalGeneration ?? null) };
      }
      if (series.name === '实际发电量') {
        return { ...series, data: trendData.map(item => item.actualGeneration ?? null) };
      }
      if (series.name === '弃光量') {
        return { ...series, data: trendData.map(item => item.curtailmentAmount ?? null) };
      }
      return {
        ...series,
        data: trendData.map(item => item.curtailmentRate ?? null),
        markLine: {
          ...curtailmentOption.series[3].markLine,
          data: markLineData,
        },
      };
    }),
  };
};

export const demandOption = {
  color: ['#16DD8E', '#FFCB47'],
  tooltip: {
    trigger: 'axis',
    renderMode: 'html',
    confine: true,
    formatter: (params: AxisTooltipParam | AxisTooltipParam[]) =>
      buildAxisTooltip(params, { 非检修月需量: ' MW', 检修月需量: ' MW' }),
  },
  legend: {
    right: 6,
    top: 0,
    itemWidth: 16,
    itemHeight: 12,
    itemGap: 16,
    textStyle: { fontSize: 14 },
    data: [
      { name: '非检修月需量', icon: 'path://M0,0H12V12H0Z M16,0L16,0' },
      { name: '检修月需量', icon: 'path://M0,0H12V12H0Z M16,0L16,0' },
    ],
  },
  xAxis: { type: 'category', data: monthLabels, axisTick: { show: false } },
  yAxis: { type: 'value', name: 'MW', splitNumber: 5 },
  series: [
    {
      name: '非检修月需量',
      type: 'bar',
      barWidth: 14,
      itemStyle: { color: '#16DD8E' },
      data: [1497.64, '-', '-', 950, 980, '-', '-', 940, 1100, 960, 930, 1080],
      markPoint: {
        symbol: 'path://M0,0H128V28H70L64,34L58,28H0Z',
        symbolSize: [128, 34],
        symbolOffset: [0, -17],
        label: {
          color: '#FFFFFF',
          fontSize: 14,
          lineHeight: 20,
          padding: [4, 8],
          offset: [0, -3],
          align: 'center',
          verticalAlign: 'middle',
          formatter: '最大:1497.64MW',
        },
        data: [{ coord: [0, 1497.64], itemStyle: { color: '#FF4D57' } }],
      },
      markLine: {
        silent: true,
        symbol: 'none',
        data: [
          {
            yAxis: 1042,
            lineStyle: { color: '#F04444', type: 'dashed' },
            label: { formatter: '基准需量:84.20MW', color: '#F04444', position: 'insideEndTop' },
          },
          {
            xAxis: 2,
            lineStyle: { color: '#52E7FF', type: 'solid' },
            label: {
              formatter: '调度运行开始月',
              color: '#FFFFFF',
              backgroundColor: 'rgba(67, 130, 140, 0.9)',
              padding: [4, 8],
              position: 'insideEndTop',
              distance: 0,
              rotate: 0,
              align: 'center',
            },
          },
        ],
      },
    },
    {
      name: '检修月需量',
      type: 'bar',
      barWidth: 14,
      barGap: '-100%',
      itemStyle: { color: '#FFCB47' },
      data: ['-', 1300, 1100, '-', '-', 1120, 1024.12, '-', '-', '-', '-', '-'],
      markPoint: {
        symbol: 'path://M0,0H128V28H70L64,34L58,28H0Z',
        symbolSize: [128, 34],
        symbolOffset: [0, -17],
        label: {
          color: '#FFFFFF',
          fontSize: 14,
          lineHeight: 20,
          padding: [4, 8],
          offset: [0, -3],
          align: 'center',
          verticalAlign: 'middle',
          formatter: '最小:1024.12MW',
        },
        data: [{ coord: [6, 1024.12], itemStyle: { color: '#45D93F' } }],
      },
    },
  ],
};

export interface DemandTrendItem {
  period?: string;
  demand?: number;
  isMaintenanceMonth?: boolean;
  isDispatchStartMonth?: boolean;
  isMaxValue?: boolean;
  isMinValue?: boolean;
}

const getDemandMarkPointWidth = (text: string) =>
  Math.max(88, Array.from(text).reduce((width, character) => width + (character.charCodeAt(0) > 255 ? 14 : 8), 0) + 16);

const getDemandMarkPoint = (trendData: DemandTrendItem[], isMaintenanceMonth: boolean) => {
  const data = trendData.flatMap((item, index) => {
    if (item.isMaintenanceMonth !== isMaintenanceMonth || item.demand == null) return [];

    if (item.isMaxValue) {
      const labelText = `最大:${formatValue(item.demand, 'MW')}`;
      return [
        {
          coord: [index, item.demand],
          value: item.demand,
          symbolSize: [getDemandMarkPointWidth(labelText), 34],
          label: { formatter: labelText },
          itemStyle: { color: '#FF4D57' },
        },
      ];
    }

    if (item.isMinValue) {
      const labelText = `最小:${formatValue(item.demand, 'MW')}`;
      return [
        {
          coord: [index, item.demand],
          value: item.demand,
          symbolSize: [getDemandMarkPointWidth(labelText), 34],
          label: { formatter: labelText },
          itemStyle: { color: '#45D93F' },
        },
      ];
    }

    return [];
  });

  return data;
};

export const getDemandOption = (trendData: DemandTrendItem[], baselineDemand?: number) => {
  const dispatchStartIndex = trendData.findIndex(item => item.isDispatchStartMonth);
  const markLineData: Array<Record<string, unknown>> = [];

  if (baselineDemand != null) {
    markLineData.push({
      yAxis: baselineDemand,
      lineStyle: { color: '#F04444', type: 'dashed' },
      label: {
        formatter: `基准需量:${formatValue(baselineDemand, 'MW', { useTenThousandUnit: false })}`,
        color: '#F04444',
        position: 'insideEndTop',
      },
    });
  }

  if (dispatchStartIndex >= 0) {
    markLineData.push({
      xAxis: dispatchStartIndex,
      lineStyle: { color: '#52E7FF', type: 'solid' },
      label: {
        formatter: '调度运行开始月',
        color: '#FFFFFF',
        backgroundColor: 'rgba(67, 130, 140, 0.9)',
        padding: [4, 8],
        position: 'insideEndTop',
        distance: 0,
        rotate: 0,
        align: 'center',
      },
    });
  }

  return {
    ...demandOption,
    tooltip: {
      ...demandOption.tooltip,
      formatter: (params: AxisTooltipParam | AxisTooltipParam[]) => {
        const list = Array.isArray(params) ? params : [params];
        const dataIndex = list.find(item => typeof item.dataIndex === 'number')?.dataIndex;
        const isMaintenanceMonth = dataIndex == null ? false : Boolean(trendData[dataIndex]?.isMaintenanceMonth);
        const seriesName = isMaintenanceMonth ? '检修月需量' : '非检修月需量';
        return buildAxisTooltip(
          list.filter(item => item.seriesName === seriesName),
          { 非检修月需量: ' MW', 检修月需量: ' MW' }
        );
      },
    },
    xAxis: {
      ...demandOption.xAxis,
      data: trendData.map(item => item.period ?? '--'),
    },
    series: demandOption.series.map((series, seriesIndex) => {
      const isMaintenanceMonth = series.name === '检修月需量';
      return {
        ...series,
        data: trendData.map(item => (item.isMaintenanceMonth === isMaintenanceMonth ? item.demand ?? null : null)),
        markPoint: {
          ...series.markPoint,
          label: {
            ...series.markPoint.label,
            formatter: undefined,
          },
          data: getDemandMarkPoint(trendData, isMaintenanceMonth),
        },
        ...(seriesIndex === 0
          ? {
              markLine: {
                ...series.markLine,
                data: markLineData,
              },
            }
          : {}),
      };
    }),
  };
};

export const accuracyOption = {
  color: ['#FFCB47', '#52E7FF'],
  tooltip: {
    trigger: 'axis',
    renderMode: 'html',
    confine: true,
    formatter: (params: AxisTooltipParam | AxisTooltipParam[]) =>
      buildAxisTooltip(params, { '准确率(1-NRMSE)': '%', '平均绝对误差(MAE)': ' MW' }),
  },
  legend: {
    right: 6,
    top: 0,
    itemWidth: 16,
    itemHeight: 4,
    data: [
      { name: '准确率(1-NRMSE)', icon: 'path://M0,0H16V4H0Z' },
      { name: '平均绝对误差(MAE)', icon: 'path://M0,0H16V4H0Z' },
    ],
  },
  grid: { left: 48, right: 48, top: 48, bottom: 36 },
  xAxis: { type: 'category', boundaryGap: false, data: monthLabels, axisTick: { show: false } },
  yAxis: [
    {
      type: 'value',
      name: '%',
      position: 'left',
      min: 0,
      max: 100,
      interval: 25,
    },
    {
      type: 'value',
      name: 'MW',
      position: 'right',
      min: 0,
      max: 40,
      interval: 10,
      alignTicks: true,
      splitLine: { show: false },
    },
  ],
  series: [
    {
      name: '准确率(1-NRMSE)',
      type: 'line',
      smooth: true,
      symbol: 'none',
      lineStyle: { width: 2 },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(255, 203, 71, 0.42)' },
            { offset: 1, color: 'rgba(255, 203, 71, 0)' },
          ],
        },
      },
      data: [88, 90, 91, 91, 91, 92, 94, 95, 95, 94, 94, 92],
    },
    {
      name: '平均绝对误差(MAE)',
      type: 'line',
      yAxisIndex: 1,
      smooth: true,
      symbol: 'none',
      lineStyle: { width: 2 },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(82, 231, 255, 0.4)' },
            { offset: 1, color: 'rgba(82, 231, 255, 0)' },
          ],
        },
      },
      data: [2, 8, 10, 12, 13, 15, 19, 23, 24, 22, 21, 17],
    },
  ],
};
